import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { Formik } from 'formik';
import { Observer } from 'mobx-react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import {
  Typography,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Menu,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { debounce } from 'debounce';
import { toJS } from 'mobx';
import { css } from '@emotion/core';
import * as yup from 'yup';
import InvalidIcon from '@material-ui/icons/Warning';
import CirclePlusIcon from '@material-ui/icons/AddCircleOutline';
import { useRootStore } from '../../../stores/BridgeProvider';
import { safeEval, formatPrice } from '../../util/UtilFunctions';
import SelectOnTabMenuItem from '../../util/SelectOnTabMenuItem';
import { CustomTable, FilterPlaceholder } from '../../common/CustomTable';
import { COLORS } from '../../util/Colors';
import SimpleSelect from '../../util/SimpleSelect';
import { NumberHandler } from '../../util/NumberHandler';
import { VoucherTransactionSchema } from '../../../validation';
import AccountSelect from '../../util/AccountSelect';
import VoucherTranTableForm from './VoucherTranTableForm';

const VoucherTransactions = ({
  VoucherId,
  VoucherType,
  VoucherTypeId,
  StatusId,
  setVoucher,
}) => {
  const { authStore, dataStore, orgStore } = useRootStore();
  const { state } = useLocation();
  const history = useHistory();
  const { transactionCategoryArray } = dataStore;
  const { settings } = orgStore;
  const { ExtSysId } = settings;
  const isIntegrated = ExtSysId > 0;
  const [isLoading, setLoading] = useState(true);
  const [voucherTransactions, setVoucherTransactions] = useState([]);
  const [isEditing, setEditing] = useState(false);
  const accounts = dataStore.validAccountArray;
  const transactionCategories = dataStore.validTransactionCategoryArray;

  const notEditable = StatusId && StatusId > 2;

  const getVoucherTransactions = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/voucher-transactions/${VoucherId}`,
      );
      // console.log(data);
      setVoucherTransactions(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchVoucher = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/voucher/${VoucherId}`,
      );
      setVoucher(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const requests = [getVoucherTransactions()];
      if (transactionCategories.length === 0) {
        requests.push(dataStore.retrieveTransactionCategories());
      }
      if (accounts.length === 0) {
        requests.push(dataStore.retrieveAccounts());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);

  const isValidAccountGroup = TypeId => {
    if ([1, 3].includes(VoucherTypeId)) {
      return [6, 3, 4, 5, 7].includes(TypeId);
    }
    if ([2, 4].includes(VoucherTypeId)) {
      return [7, 3, 4, 5, 6].includes(TypeId);
    }
    return true;
  };

  const wipeChanges = () => {
    getVoucherTransactions();
    setEditing(false);
  };

  const newTransaction = (clone = {}) => {
    const vtsClone = [...voucherTransactions];
    vtsClone.push({
      VoucherId,
      CategoryId: clone.CategoryId || null,
      AccountId: clone.AccountId || '',
      Price: clone.Price || '',
      Quantity: clone.Quantity || 1,
      Description: clone.Description || '',
      dirty: true,
    });
    setVoucherTransactions(vtsClone);
  };

  useEffect(() => {
    const { voucherTranEditMode } = state || {};
    if (!isLoading && voucherTranEditMode) {
      setEditing(true);
      newTransaction();
    }
  }, [isLoading]);

  if (isLoading) return null;

  // Have to self invoke to bind context
  const saveVoucherTransaction = async (voucherTransactionToUpdate, i) => {
    try {
      const { status } = await authStore.request.post(
        `${process.env.REACT_APP_API}/api/voucher-transaction`,
        {
          VoucherTransactionId: voucherTransactionToUpdate.VoucherTranId,
          VoucherId: voucherTransactionToUpdate.VoucherId,
          CategoryId: voucherTransactionToUpdate.CategoryId,
          AccountId: voucherTransactionToUpdate.AccountId,
          Price: voucherTransactionToUpdate.Price,
          Quantity: voucherTransactionToUpdate.Quantity,
          Description: voucherTransactionToUpdate.Description,
        },
      );
      if (status === 200) {
        const vtsClone = [...voucherTransactions];
        const vtClone = vtsClone[i];
        vtClone.dirty = false;
        setVoucherTransactions(vtsClone);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const saveVoucherTransactions = async () => {
    const vtsClone = [...voucherTransactions];
    let foundErrors = false;
    const requests = [];
    await Promise.all(
      vtsClone.map(async (vt, i) => {
        if (vt.dirty) {
          try {
            await VoucherTransactionSchema.validate(
              { ...vt, isIntegrated },
              { abortEarly: false },
            );
            requests.push(saveVoucherTransaction(vt, i));
            vt.errors = null;
          } catch (e) {
            console.error(e);
            if (e.errors) {
              foundErrors = true;
              vt.errors = e.errors;
            }
          }
        }
      }),
    );

    await Promise.all(requests);
    if (!foundErrors) {
      wipeChanges();
    } else {
      setVoucherTransactions(vtsClone);
    }

    await fetchVoucher();
  };

  const deleteVoucherTransaction = async cellInfo => {
    const voucherTransaction = voucherTransactions[cellInfo.index];
    const { VoucherTranId } = voucherTransaction;
    if (VoucherTranId) {
      try {
        await authStore.request.delete(
          `${process.env.REACT_APP_API}/api/voucher-transaction`,
          {
            data: { VoucherTransactionId: VoucherTranId },
          },
        );
      } catch (e) {
        console.error(e);
      }
    }
    const vtsClone = [...voucherTransactions];
    vtsClone.splice(cellInfo.index, 1);
    setVoucherTransactions(vtsClone);
  };

  const validateValue = async ({ valueName, value }) => {
    const result = await yup
      .reach(VoucherTransactionSchema, valueName)
      .isValid(value);
    return {
      isValid: result,
      error: result ? null : `Error validating ${valueName}`,
    };
  };

  const handleChange = async (cellInfo, value, valueName) => {
    const vtsClone = [...voucherTransactions];
    const vtClone = vtsClone[cellInfo.index];
    vtClone[valueName] = value;
    vtClone.dirty = true;

    const { isValid, error } = await validateValue({ valueName, value });

    if (!isValid) {
      vtClone.hasError = error;
      //   console.log('HAS ERROR', vtClone);
    } else if (vtClone.hasError) {
      vtClone.hasError = null;
    }
    setVoucherTransactions(vtsClone);
  };

  const handleChangeTranCategory = (cellInfo, value, valueName) => {
    handleChange(cellInfo, value, valueName);
    // After setting tran category, it updates the account
    const vtsClone = [...voucherTransactions];
    const vtClone = vtsClone[cellInfo.index];

    if (value === null) {
      setVoucherTransactions(vtsClone);
      return;
    }
    try {
      const {
        APContraAccountId,
        ARContraAccountId,
        Name,
        Code,
      } = dataStore.getTranCategoryFromId(value);

      if (VoucherType === 'Receivable') {
        vtClone.AccountId = ARContraAccountId;
      }
      if (VoucherType === 'Payable') {
        vtClone.AccountId = APContraAccountId;
      }
      vtClone.Description = Name || Code;
      setVoucherTransactions(vtsClone);
    } catch (e) {
      console.error(e);
    }
  };

  const filteredTranCategories = cellInfo => {
    const voucherTransaction = voucherTransactions[cellInfo.index];
    const curId = voucherTransaction.CategoryId;

    return transactionCategories.filter(
      ({
        APAccountId,
        APContraAccountId,
        ARAccountId,
        ARContraAccountId,
        TranCategoryId,
      }) => {
        // If the category is already set then it should exist in the dropdown
        if (curId && curId === TranCategoryId) {
          return true;
        }
        if (!VoucherType) return true;
        if ([1, 3, 5].includes(VoucherTypeId)) {
          return ARAccountId && ARContraAccountId;
        }
        if ([2, 4, 6].includes(VoucherTypeId)) {
          return APAccountId && APContraAccountId;
        }

        return false;
      },
    );
  };

  const moveAccountTypeToFront = (list, typeId) => {
    const index = list.findIndex(({ id }) => id === typeId);
    list.splice(0, 0, list.splice(index, 1)[0]);
    return list;
  };

  const EditableCell = props => {
    const { cellInfo, valueName, ...rest } = props;
    const voucherTransaction = voucherTransactions[cellInfo.index];
    const cellVal = voucherTransaction[valueName];
    const [curVal, setCurVal] = useState(cellVal);
    return (
      <TextField
        key={cellInfo}
        name={valueName}
        label={valueName}
        type="text"
        value={curVal}
        margin="normal"
        onChange={e => setCurVal(e.target.value)}
        onBlur={() =>
          curVal !== cellVal ? handleChange(cellInfo, curVal, valueName) : null
        }
        variant="outlined"
        fullWidth
        {...rest}
      />
    );
  };

  const EditableSelectCell = ({
    cellInfo,
    valueName,
    values,
    changeFunction,
  }) => {
    const voucherTransaction = voucherTransactions[cellInfo.index];
    const cellVal = voucherTransaction[valueName];
    const changeHandler = changeFunction || handleChange;
    return (
      <div>
        <SimpleSelect
          label="Category"
          handleChange={e => changeHandler(cellInfo, e.target.value, valueName)}
          value={cellVal}
          name="type"
        >
          <SelectOnTabMenuItem value={null}>
            <em>None</em>
          </SelectOnTabMenuItem>
          {values.map(({ TranCategoryId, Code }) => (
            <SelectOnTabMenuItem key={TranCategoryId} value={TranCategoryId}>
              {Code}
            </SelectOnTabMenuItem>
          ))}
        </SimpleSelect>
        <div>{voucherTransactions[cellInfo.index]?.[`${valueName}_error`]}</div>
      </div>
    );
  };

  const EditableNumberCell = props => {
    const { cellInfo, valueName, money, ...rest } = props;
    const voucherTransaction = voucherTransactions[cellInfo.index];
    const parseNumberVal = val => {
      if (val === '') return val;
      if (money) return formatPrice(val);
      return parseInt(val, 10);
    };
    const cellVal = parseNumberVal(voucherTransaction[valueName]);
    const [curVal, setCurVal] = useState(cellVal);
    return (
      <TextField
        key={cellInfo}
        name={valueName}
        label={valueName}
        value={curVal}
        margin="normal"
        onChange={e => setCurVal(e.target.value)}
        onBlur={() =>
          curVal !== cellVal ? handleChange(cellInfo, curVal, valueName) : null
        }
        variant="outlined"
        fullWidth
        InputProps={{
          startAdornment: money ? (
            <InputAdornment position="start">$</InputAdornment>
          ) : null,
        }}
        {...rest}
      />
    );
  };

  const EditableAccountCell = props => {
    const { cellInfo, ...rest } = props;
    const voucherTransaction = voucherTransactions[cellInfo.index];
    const { AccountId } = voucherTransaction;
    const Account = dataStore.getAccountFromId(AccountId);

    return (
      <AccountSelect
        {...rest}
        value={Account}
        isValidAccountGroup={isValidAccountGroup}
        sortFunction={list => {
          if ([1, 3].includes(VoucherTypeId)) {
            return moveAccountTypeToFront(list, 6);
          }
          if ([2, 4].includes(VoucherTypeId)) {
            return moveAccountTypeToFront(list, 7);
          }
          return list;
        }}
        onChange={val =>
          handleChange(
            cellInfo,
            safeEval(() => val.AccountId, null),
            'AccountId',
          )
        }
      />
    );
  };

  const VoucherTranDropdown = ({ cellInfo }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const voucherTransaction = voucherTransactions[cellInfo.index];

    const open = Boolean(anchorEl);

    const handleClick = event => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <Fragment>
        <IconButton
          onClick={e => handleClick(e)}
          style={{ padding: 0, height: 'fit-content' }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={() => deleteVoucherTransaction(cellInfo)}>
            Delete Transaction
          </MenuItem>
          <MenuItem onClick={() => newTransaction(voucherTransaction)}>
            Clone Transaction
          </MenuItem>
        </Menu>
      </Fragment>
    );
  };

  const ValidationErrors = ({ cellInfo }) => {
    const voucherTransaction = voucherTransactions[cellInfo.index];
    const { errors } = voucherTransaction;

    if (!errors) return null;

    return (
      <Tooltip
        title={
          <ul style={{ padding: 0, listStyleType: 'none' }}>
            {errors.map(error => (
              <li key={error}>* {error}</li>
            ))}
          </ul>
        }
      >
        <InvalidIcon
          style={{
            color: COLORS.red,
          }}
        />
      </Tooltip>
    );
  };

  const columns = [
    {
      // Header: null,
      Header: () => null,
      width: 30,
      Cell: ({ row }) => <ValidationErrors cellInfo={row} />,
      id: 1,
      show: isEditing,
    },
    {
      Header: 'Category',
      width: 90,
      Cell: ({ cell }) => {
        return cell.value;
      },
      accessor: d =>
        safeEval(
          () =>
            transactionCategoryArray.find(
              e => e.TranCategoryId === d.CategoryId,
            ).Code,
          'N/A',
        ),
      id: 2,
      Filter: FilterPlaceholder,
    },
    {
      Header: 'Account',
      width: 120,
      Cell: ({ cell }) => {
        return cell.value || null;
      },
      accessor: d => d.AccountName,
    },
    {
      Header: 'Price',
      // maxWidth: 120,
      width: 60,
      Cell: ({ row }) => (
        <NumberHandler
          num={row.original.Price}
          currency="$"
          justify="flex-end"
          float={2}
        />
      ),

      accessor: d => d.Price || 'N/A',
      id: 3,
    },
    {
      Header: 'Quantity',
      // maxWidth: 100,
      width: 60,
      Cell: ({ cell }) => {
        return cell.value;
      },
      accessor: d => (d.Quantity ? parseInt(d.Quantity, 10) : 'N/A'),
      id: 4,
    },
    {
      Header: 'Total',
      // maxWidth: 120,
      // width: 80,
      width: 60,
      // FIXME: Price - make sure these are numbers!
      // Also currently 0.3333 x 3 = 1.00 which is bad
      Cell: ({ row }) => (
        <NumberHandler
          num={row.original.Quantity * row.original.Price}
          currency="$"
          justify="flex-end"
          float={2}
        />
      ),

      accessor: d => d.Amount || 'N/A',
      id: 5,
    },
    {
      Header: 'Description',
      // maxWidth: isEditing ? 150 : null,
      Cell: ({ cell }) => {
        return cell.value;
      },

      accessor: 'Description',
      id: 6,
    },
    {
      Header: '',
      width: 30,
      Cell: ({ row }) => <VoucherTranDropdown cellInfo={row} />,
      id: 7,
      show: isEditing,
    },
  ];

  const editDetailsButton = () => {
    setEditing(true);
    if (voucherTransactions && voucherTransactions.length === 0) {
      newTransaction();
    }
  };

  return (
    <Observer
      render={() => {
        return (
          <>
            {!isEditing ? (
              <CustomTable
                data={voucherTransactions || []}
                columns={columns}
                disableQueryParams
                minRows={1}
                trClassName={!isEditing && 'react-table-hover-row'}
                tableHeader={
                  <Typography variant="h4">Transaction Details</Typography>
                }
                actionables={
                  !isEditing ? (
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={editDetailsButton}
                      disabled={notEditable}
                    >
                      Edit Details
                    </Button>
                  ) : (
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={wipeChanges}
                    >
                      Cancel
                    </Button>
                  )
                }
                getTrProps={({ row }) => ({
                  onClick: () =>
                    history.push(
                      `/vouchers/voucher-transaction/${row.original.VoucherTranId}/edit`,
                    ),
                })}
              />
            ) : (
              <VoucherTranTableForm
                initialVal={voucherTransactions}
                VoucherId={VoucherId}
                wipeChanges={wipeChanges}
                setVoucherTransactions={setVoucherTransactions}
                VoucherType={VoucherType}
                VoucherTypeId={VoucherTypeId}
              />
            )}
          </>
        );
      }}
    />
  );
};

export default VoucherTransactions;
